import React, {useState, useEffect} from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
} from '@mui/material';
import {toast} from 'react-toastify';
import {adminApi} from 'utils/api';

const styles = {
    header: {
        mb: 2,
    },
    form: {
        display: 'flex',
        gap: 2,
        mb: 3,
        alignItems: 'flex-end',
    },
    tableContainer: {
        maxHeight: 400,
    },
};

export default function ProductsTab() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    const [name, setName] = useState('');
    const [version, setVersion] = useState('1.0');
    const [description, setDescription] = useState('');
    const [type, setType] = useState('INTERNAL');
    const [submitting, setSubmitting] = useState(false);

    const [editOpen, setEditOpen] = useState(false);
    const [editProduct, setEditProduct] = useState(null);
    const [editName, setEditName] = useState('');
    const [editVersion, setEditVersion] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editType, setEditType] = useState('INTERNAL');
    const [editSubmitting, setEditSubmitting] = useState(false);

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            const {data} = await adminApi.getProducts();
            setProducts(data.products || []);
        } catch (err) {
            toast.error('제품 목록 로드 실패');
        } finally {
            setLoading(false);
        }
    };

    const handleAddProduct = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            await adminApi.createProduct({name, version, description, type});
            toast.success('제품이 추가되었습니다.');
            setName('');
            setVersion('1.0');
            setDescription('');
            setType('INTERNAL');
            loadProducts();
        } catch (err) {
            toast.error(err.response?.data?.error || '제품 추가 실패');
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenEdit = (product) => {
        setEditProduct(product);
        setEditName(product.name);
        setEditVersion(product.version || '');
        setEditDescription(product.description || '');
        setEditType(product.type || 'INTERNAL');
        setEditOpen(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditSubmitting(true);

        try {
            await adminApi.updateProduct(editProduct.id, {
                name: editName,
                version: editVersion,
                description: editDescription,
                type: editType,
            });
            toast.success('제품이 수정되었습니다.');
            setEditOpen(false);
            loadProducts();
        } catch (err) {
            toast.error(err.response?.data?.error || '제품 수정 실패');
        } finally {
            setEditSubmitting(false);
        }
    };

    const handleDeleteProduct = async (productId, productName) => {
        if (!window.confirm(`${productName} 제품을 삭제하시겠습니까?`)) return;

        try {
            await adminApi.deleteProduct(productId);
            toast.success('제품이 삭제되었습니다.');
            loadProducts();
        } catch (err) {
            toast.error(err.response?.data?.error || '제품 삭제 실패');
        }
    };

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                <CircularProgress size={32}/>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h2" sx={styles.header}>
                제품 목록
            </Typography>

            <Box component="form" onSubmit={handleAddProduct} sx={styles.form}>
                <TextField
                    label="제품명"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    size="small"
                    sx={{width: 200}}
                />
                <TextField
                    label="버전"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    size="small"
                    sx={{width: 120}}
                />
                <TextField
                    label="설명"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    size="small"
                    sx={{width: 300}}
                />
                <FormControl size="small" sx={{minWidth: 130}}>
                    <InputLabel>타입</InputLabel>
                    <Select
                        value={type}
                        label="타입"
                        onChange={(e) => setType(e.target.value)}
                    >
                        <MenuItem value="INTERNAL">자사제품</MenuItem>
                        <MenuItem value="INTERNAL_MODULE">자사제품(모듈)</MenuItem>
                        <MenuItem value="EXTERNAL">기타제품</MenuItem>
                    </Select>
                </FormControl>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                >
                    추가
                </Button>
            </Box>

            <TableContainer sx={styles.tableContainer}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>제품명</TableCell>
                            <TableCell>타입</TableCell>
                            <TableCell>버전</TableCell>
                            <TableCell>설명</TableCell>
                            <TableCell>생성일</TableCell>
                            <TableCell>액션</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {products.map((product) => (
                            <TableRow key={product.id} hover>
                                <TableCell>{product.name}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={product.type === 'EXTERNAL'
                                            ? '기타'
                                            : product.type === 'INTERNAL_MODULE' ? '자사(모듈)' : '자사'}
                                        size="small"
                                        color={product.type === 'EXTERNAL' ? 'warning' : 'primary'}
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell>{product.version}</TableCell>
                                <TableCell>{product.description || '-'}</TableCell>
                                <TableCell>
                                    {new Date(product.created_at).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                    <Box sx={{display: 'flex', gap: 1}}>
                                        <Button
                                            size="small"
                                            onClick={() => handleOpenEdit(product)}
                                        >
                                            수정
                                        </Button>
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => handleDeleteProduct(product.id, product.name)}
                                        >
                                            삭제
                                        </Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* 제품 수정 다이얼로그 */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>제품 수정</DialogTitle>
                <DialogContent>
                    <Box
                        component="form"
                        id="edit-product-form"
                        onSubmit={handleEditSubmit}
                        sx={{display: 'flex', flexDirection: 'column', gap: 2, mt: 1}}
                    >
                        <TextField
                            label="제품명"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            required
                            size="small"
                            fullWidth
                        />
                        <TextField
                            label="버전"
                            value={editVersion}
                            onChange={(e) => setEditVersion(e.target.value)}
                            size="small"
                            fullWidth
                        />
                        <TextField
                            label="설명"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                        />
                        <FormControl size="small" fullWidth>
                            <InputLabel>타입</InputLabel>
                            <Select
                                value={editType}
                                label="타입"
                                onChange={(e) => setEditType(e.target.value)}
                            >
                                <MenuItem value="INTERNAL">자사제품</MenuItem>
                                <MenuItem value="INTERNAL_MODULE">자사제품(모듈)</MenuItem>
                                <MenuItem value="EXTERNAL">기타제품</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>취소</Button>
                    <Button
                        type="submit"
                        form="edit-product-form"
                        variant="contained"
                        disabled={editSubmitting}
                    >
                        저장
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
